import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Layers, Plus, RefreshCw, ScanSearch, Trash2 } from 'lucide-react';
import {
  expandVlanRanges,
  type CliActionResult,
  type OltVlan,
  type OltVlanListResponse,
  type Page,
  type PonPort,
  vlanSelectableForOnus,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useTasks } from '../../lib/tasks';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../../components/ui';

interface DetectVerdict {
  vlanId: number;
  /** DISTINCT ONUs. An ONU on two virtual ports is one ONU. */
  onuCount: number;
  /** What the chassis printed: one entry per (ONU, virtual port) pair. */
  entryCount: number;
  vports: number[];
  onusByVport: Record<number, number>;
  onusAboveVport1: number;
  isManagement: boolean;
  reason: string;
}

interface DetectResponse {
  vendorSupported: boolean;
  items: DetectVerdict[];
  applied: number;
  warnings: string[];
}

interface Classification {
  description: string;
  usedForIptv: boolean;
  usedForMgmt: boolean;
  onuSelectable?: boolean | null;
  dhcpSnooping: boolean;
  lanToLan: boolean;
  defaultPonPorts: string[];
}

const BLANK: Classification = {
  description: '',
  usedForIptv: false,
  usedForMgmt: false,
  onuSelectable: null as boolean | null,
  dhcpSnooping: false,
  lanToLan: false,
  defaultPonPorts: [],
};

/**
 * The VLANs one OLT carries.
 *
 * A VLAN row holds two kinds of fact and the tab keeps them visibly apart. Its
 * existence and its description are chassis configuration — the description is
 * saved here AND written onto the OLT, so `show vlan <id>` and this table never
 * disagree — and every one of those writes previews the exact CLI first. What
 * the VLAN is used for (IPTV, management, LAN-to-LAN, which PON ports default
 * to it) has no chassis command at all, so it is SimpleACS's own record and the
 * form says as much rather than implying a change that never happens.
 */
export function OltVlansTab({ oltId, oltName }: { oltId: string; oltName: string }) {
  const { can } = useSession();
  const canManage = can('olt.manage');
  /*
    Detect is the odd one out on this bar. It opens an SSH session and reads how
    the ONUs are wired — nothing reaches the chassis, only SimpleACS's own note
    of what each VLAN is FOR changes — so it is `olt.diagnose` rather than the
    `olt.manage` that adding and deleting a VLAN needs. That difference in blast
    radius is exactly why the two were split apart on 2026-08-30.
  */
  const canDetect = can('olt.diagnose');
  const [editing, setEditing] = useState<OltVlan | null>(null);
  const [adding, setAdding] = useState<'one' | 'many' | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const query = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<OltVlanListResponse>(`/olts/${encodeURIComponent(oltId)}/vlans`),
  });
  const vlans = query.data?.items ?? [];
  /**
   * The tenant's answer about VLAN 1, from the same response as the rows.
   *
   * The Offered column below is the one place an operator can SEE this rule
   * rather than remember it, so it has to be judged with everything the rule
   * uses — a column that computed a stale "hidden" against a fresh setting
   * would be worse than no column.
   */
  const vlan1Enabled = query.data?.vlan1Selectable === true;

  function toggle(vlanId: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vlanId)) next.delete(vlanId);
      else next.add(vlanId);
      return next;
    });
  }

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Layers className="size-4 text-muted" />
            VLANs on {oltName}
            <span className="text-xs font-normal text-faint">{vlans.length}</span>
          </span>
        }
        action={
          (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                icon={<RefreshCw className="size-3.5" />}
                loading={query.isFetching}
                onClick={() => query.refetch()}
                title="Re-read this OLT's VLANs"
              >
                Refresh
              </Button>
              {canManage && (
                <Button size="sm" variant="primary" icon={<Plus className="size-3.5" />} onClick={() => setAdding('one')}>
                  Add VLAN
                </Button>
              )}
              {canManage && (
                <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setAdding('many')}>
                  Add multiple
                </Button>
              )}
              {canDetect && (
                <Button
                  size="sm"
                  icon={<ScanSearch className="size-3.5" />}
                  onClick={() => setDetecting(true)}
                  title="Read how the ONUs are wired and find the management VLAN"
                >
                  Detect mngt
                </Button>
              )}
              {canManage && (
                <Button
                  size="sm"
                  variant="danger"
                  icon={<Trash2 className="size-3.5" />}
                  disabled={selected.size === 0}
                  onClick={() => setDeleting(true)}
                >
                  Delete {selected.size > 0 ? selected.size : ''}
                </Button>
              )}
            </div>
          )
        }
      >
        <p className="border-b border-line px-5 py-2 text-xs text-faint">
          Adding a VLAN here does not tag it on the uplinks. Use the Uplinks tab to carry it out of
          the chassis, or it will never reach the network.
        </p>

        {query.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : query.error ? (
          <div className="p-5">
            <ErrorNotice error={query.error} />
          </div>
        ) : vlans.length === 0 ? (
          <EmptyState
            icon={<Layers className="size-8" />}
            title="No VLANs recorded"
            description="Run Sync inventory on this OLT to read its VLAN table, or add one here."
          />
        ) : (
          <Table>
            <THead>
              <th className="th w-8" />
              <th className="th">VLAN-ID</th>
              <th className="th">Default for</th>
              <th className="th">Description</th>
              <th className="th">IPTV</th>
              <th className="th">Mgmt/VoIP</th>
              <th className="th">DHCP snooping</th>
              <th className="th">LAN to LAN</th>
              <th className="th">Subscribers</th>
              <th className="th">ONUs</th>
            </THead>
            <TBody>
              {vlans.map((vlan) => (
                <TRow key={vlan.id} onClick={canManage ? () => setEditing(vlan) : undefined}>
                  <td className="td" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(vlan.vlanId)}
                      disabled={!canManage}
                      onChange={() => toggle(vlan.vlanId)}
                    />
                  </td>
                  <td className="td font-mono text-xs font-medium text-ink">
                    <span className="flex items-center gap-1.5">
                      {vlan.vlanId}
                      {vlan.uplinks.length === 0 && (
                        <AlertTriangle
                          className="size-3.5 text-warn"
                          aria-label="Not tagged on any uplink"
                        />
                      )}
                      {vlan.source === 'manual' && (
                        <span
                          className="text-[10px] uppercase tracking-wide text-faint"
                          title="Added here, not yet seen in a sweep of the chassis"
                        >
                          unconfirmed
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="td text-xs text-muted">
                    {vlan.defaultPonPorts.length === 0
                      ? 'None selected'
                      : vlan.defaultPonPorts.length <= 3
                        ? vlan.defaultPonPorts.join(', ')
                        : `${vlan.defaultPonPorts.length} PON ports`}
                  </td>
                  <td className="td font-mono text-xs text-ink">
                    {vlan.description ?? ''}
                    {/* A management VLAN's subnet, as the onboarding setup recorded it. */}
                    {vlan.usedForMgmt && vlan.subnet ? (
                      <span className="ml-2 text-faint" title="The subnet this management VLAN's ONUs take their addresses from">
                        {vlan.subnet}
                      </span>
                    ) : null}
                  </td>
                  <Tick on={vlan.usedForIptv} />
                  <Tick on={vlan.usedForMgmt} />
                  <Tick on={vlan.dhcpSnooping} />
                  <Tick on={vlan.lanToLan} />
                  {/* Whether this VLAN is offered when attaching a subscriber.
                      Worth a column rather than a rule people have to recall:
                      the two hidden cases look nothing alike. */}
                  <td className="td text-xs">
                    {vlan.usedForMgmt ? (
                      <span className="text-faint" title="A management VLAN is never offered to subscribers">
                        management
                      </span>
                    ) : vlanSelectableForOnus(
                        {
                          vlanId: vlan.vlanId,
                          usedForMgmt: vlan.usedForMgmt,
                          onuSelectable: vlan.onuSelectable ?? null,
                        },
                        { vlan1Enabled },
                      ) ? (
                      <span className="text-muted">offered</span>
                    ) : vlan.vlanId === 1 && !vlan1Enabled && vlan.onuSelectable !== false ? (
                      /* Named apart from a plain "hidden": this one is the
                         organization's setting rather than this chassis's own
                         tick, and the two are undone on different screens. */
                      <span
                        className="text-warn"
                        title="VLAN 1 is off for this organization, so it is not offered to new subscribers here. ONUs already on it keep working and stay editable. Turn it on in the organization's settings, or tick this row for this chassis alone."
                      >
                        off for the org
                      </span>
                    ) : (
                      <span className="text-warn" title="Hidden from subscriber VLAN pickers">
                        hidden
                      </span>
                    )}
                  </td>
                  {/* The chassis figure when we have one: on a fleet SimpleACS
                      did not provision, its own count is correctly zero and
                      tells an operator nothing. */}
                  {/* Two different figures, and they answer different
                      questions. `onuCount` is what SimpleACS put here, which is
                      drillable because it knows which ONUs. `chassisOnuCount`
                      is what the OLT says is here — the honest number on a
                      fleet this platform did not provision, and not drillable,
                      because a count is all the chassis gave. */}
                  <td className="td tabular-nums text-muted">
                    {vlan.onuCount > 0 ? (
                      <Link
                        to={`/onus?oltId=${encodeURIComponent(oltId)}&vlan=${vlan.vlanId}`}
                        className="text-link hover:underline"
                        title={`Show the ONUs SimpleACS has on VLAN ${vlan.vlanId}`}
                      >
                        {vlan.onuCount}
                      </Link>
                    ) : vlan.chassisOnuCount !== null && vlan.chassisOnuCount !== undefined ? (
                      /* The age goes on the number's face, not into a tooltip.
                         This column is written by one thing only — Detect
                         management VLAN — so it is exactly as old as the last
                         time somebody pressed that button, and on this fleet
                         that is months. A stale count read as current is how a
                         VLAN gets sized off a figure nobody has refreshed. */
                      <span
                        className="text-muted"
                        title={`Read off the chassis${vlan.chassisOnuCountAt ? ` ${formatRelative(vlan.chassisOnuCountAt)}` : ''} by Detect management VLAN, which is the only thing that writes it. It gave a number, not a list, so there is nothing to open. Run Detect again for a current figure.`}
                      >
                        {vlan.chassisOnuCount}
                        <span className="ml-1 text-[10px] uppercase text-faint">on the OLT</span>
                        <span className="ml-1 text-[10px] text-faint">
                          {vlan.chassisOnuCountAt
                            ? `read ${formatRelative(vlan.chassisOnuCountAt)}`
                            : 'read date unknown'}
                        </span>
                      </span>
                    ) : (
                      <span className="text-faint" title="Not counted yet — run Detect management VLAN, which counts as it reads">
                        —
                      </span>
                    )}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      {editing && (
        <VlanDialog oltId={oltId} oltName={oltName} vlan={editing} onClose={() => setEditing(null)} />
      )}
      {adding && (
        <VlanDialog oltId={oltId} oltName={oltName} mode={adding} onClose={() => setAdding(null)} />
      )}
      {detecting && (
        <DetectDialog oltId={oltId} oltName={oltName} onClose={() => setDetecting(false)} />
      )}
      {deleting && (
        <DeleteVlansDialog
          oltId={oltId}
          oltName={oltName}
          vlanIds={[...selected]}
          onClose={() => setDeleting(false)}
          onDone={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}

function Tick({ on }: { on: boolean }) {
  return (
    <td className="td">
      <input type="checkbox" checked={on} readOnly className="pointer-events-none" />
    </td>
  );
}

/**
 * Add or edit a VLAN. The classification half saves with one click; anything
 * that changes the chassis (creating it, renaming it) needs a second,
 * deliberate click. Either way the dialog CLOSES on the click and the write
 * runs in the tasks tray.
 */
function VlanDialog({
  oltId,
  oltName,
  vlan,
  mode,
  onClose,
}: {
  oltId: string;
  oltName: string;
  vlan?: OltVlan;
  mode?: 'one' | 'many';
  onClose: () => void;
}) {
  const tasks = useTasks();
  const editing = Boolean(vlan);
  const many = mode === 'many';

  const [vlanId, setVlanId] = useState(vlan ? String(vlan.vlanId) : '');
  const [range, setRange] = useState('');
  const [descriptionPrefix, setDescriptionPrefix] = useState('');
  const [flags, setFlags] = useState<Classification>(
    vlan
      ? {
          description: vlan.description ?? '',
          usedForIptv: vlan.usedForIptv,
          usedForMgmt: vlan.usedForMgmt,
          onuSelectable: vlan.onuSelectable ?? null,
          dhcpSnooping: vlan.dhcpSnooping,
          lanToLan: vlan.lanToLan,
          defaultPonPorts: vlan.defaultPonPorts,
        }
      : BLANK,
  );
  const [showPorts, setShowPorts] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const portsQuery = useQuery({
    queryKey: ['olt', oltId, 'pon-ports'],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${encodeURIComponent(oltId)}/pon-ports`),
    enabled: showPorts,
  });

  const expanded = useMemo(() => expandVlanRanges(range), [range]);
  // Only a changed description reaches the chassis; the rest saves silently.
  const describing = editing && flags.description.trim() !== (vlan?.description ?? '');

  const base = `/olts/${encodeURIComponent(oltId)}/vlans`;
  function body(dryRun: boolean) {
    const shared = { ...flags, description: flags.description.trim() || null, dryRun };
    if (many) {
      return {
        ...shared,
        description: undefined,
        vlans: range,
        descriptionPrefix: descriptionPrefix.trim() || null,
      };
    }
    if (editing) return shared;
    return { ...shared, vlanId: Number(vlanId) };
  }

  // Editing classification alone touches nothing on the chassis, so it saves
  // straight away rather than pretending a preview is needed — but still
  // through the tray, like the chassis writes below, so the dialog is gone
  // the moment Save is pressed and a refused save stays on screen.
  const startSaveClassification = () => {
    const id = vlan!.vlanId;
    const payload = body(false);
    onClose();
    tasks.start({
      label: `Save VLAN ${id} on ${oltName}`,
      run: () => api.patch(`${base}/${id}`, payload),
      done: () => `VLAN ${id} on ${oltName} — what it is used for is saved.`,
      invalidate: [['olt', oltId, 'vlans']],
    });
  };

  const needsChassisWrite = !editing || describing;
  const title = editing
    ? `VLAN ${vlan!.vlanId}`
    : many
      ? 'Add multiple VLANs'
      : 'Add VLAN';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-xs text-muted">
            The VLAN and its description are written to the OLT as well as saved here. What the
            VLAN is used for is SimpleACS's own record and shapes what the ONU pickers offer.
          </p>
        </header>

        <div className="space-y-4 p-5">
          {many ? (
                <>
                  <Field
                    label="VLAN-IDs"
                    hint={
                      expanded.length > 0
                        ? `${expanded.length} VLANs: ${expanded.slice(0, 12).join(', ')}${expanded.length > 12 ? '…' : ''}`
                        : 'A range or a list — "50-60" or "50,51,80"'
                    }
                  >
                    <Input
                      value={range}
                      onChange={(e) => {
                        setRange(e.target.value);
                        setConfirming(false);
                      }}
                      placeholder="50-60"
                      className="font-mono"
                    />
                  </Field>
                  <Field
                    label="Description prefix"
                    hint="Each VLAN is described as prefix + its id. Blank leaves them undescribed."
                  >
                    <Input
                      value={descriptionPrefix}
                      onChange={(e) => {
                        setDescriptionPrefix(e.target.value);
                        setConfirming(false);
                      }}
                      placeholder="cvlan_"
                      className="font-mono"
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="VLAN-ID">
                    <Input
                      value={vlanId}
                      onChange={(e) => {
                        setVlanId(e.target.value);
                        setConfirming(false);
                      }}
                      disabled={editing}
                      placeholder="51"
                      className="font-mono"
                    />
                  </Field>

                </>
              )}

              {!many && (
                <Field
                  label="Description"
                  hint="Saved here and written onto the OLT. Letters, digits, spaces, dot, dash and underscore."
                >
                  <Input
                    value={flags.description}
                    onChange={(e) => {
                      setFlags({ ...flags, description: e.target.value });
                      setConfirming(false);
                    }}
                    placeholder="cvlan_u3"
                    className="font-mono"
                  />
                </Field>
              )}

              <fieldset className="space-y-2 rounded-lg border border-line p-4">
                <legend className="px-1 text-xs uppercase tracking-wide text-faint">
                  What this VLAN is for
                </legend>
                <Check
                  label="Multicast VLAN, used for IPTV"
                  checked={flags.usedForIptv}
                  onChange={(v) => setFlags({ ...flags, usedForIptv: v })}
                />
                <Check
                  label="Management/VoIP VLAN"
                  checked={flags.usedForMgmt}
                  onChange={(v) => setFlags({ ...flags, usedForMgmt: v })}
                />
                <Check
                  label="DHCP snooping on VLAN"
                  checked={flags.dhcpSnooping}
                  onChange={(v) => setFlags({ ...flags, dhcpSnooping: v })}
                />
                <Check
                  label="Enable direct communication between ONUs on this VLAN (LAN-to-LAN)"
                  checked={flags.lanToLan}
                  onChange={(v) => setFlags({ ...flags, lanToLan: v })}
                />
                {/* VLAN 1 is the only VLAN hidden from subscriber pickers by
                    default, so it is the only one that needs this control. */}
                {Number(vlanId) === 1 && (
                  <div className="mt-1 rounded-lg border border-warn/30 bg-warn-soft p-3">
                    <Check
                      label="Allow subscribers to be put on VLAN 1"
                      checked={flags.onuSelectable === true}
                      onChange={(v) => setFlags({ ...flags, onuSelectable: v ? true : null })}
                    />
                    <p className="mt-1 text-xs text-warn">
                      Not recommended. VLAN 1 is every switch&rsquo;s untagged default and carries
                      whatever nobody classified, so a subscriber on it is indistinguishable from
                      one that was never configured — and their traffic shares a VLAN with the
                      chassis&rsquo;s own. Turn this on only if this network genuinely sells
                      service on VLAN 1.
                    </p>
                  </div>
                )}
                <p className="pt-1 text-xs text-faint">
                  Recorded in SimpleACS. A VLAN carries no such flag on the chassis — what makes
                  one the management VLAN is that every ONU carries it on its management
                  service-port, so this is where that fact is written down. Management VLANs are
                  the ones offered when pointing an ONU at TR-069, and are never offered as a
                  subscriber VLAN.
                </p>
              </fieldset>

              <div>
                <Button size="sm" onClick={() => setShowPorts((s) => !s)}>
                  {showPorts ? 'Hide PON ports' : 'Default for PON ports'}
                </Button>
                {showPorts && (
                  <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-line p-3">
                    {portsQuery.isLoading ? (
                      <Spinner />
                    ) : (
                      <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
                        {(portsQuery.data?.items ?? []).map((p) => (
                          <label
                            key={p.port}
                            className="flex cursor-pointer items-center gap-2 font-mono text-xs text-ink"
                          >
                            <input
                              type="checkbox"
                              checked={flags.defaultPonPorts.includes(p.port)}
                              onChange={(e) =>
                                setFlags({
                                  ...flags,
                                  defaultPonPorts: e.target.checked
                                    ? [...flags.defaultPonPorts, p.port]
                                    : flags.defaultPonPorts.filter((x) => x !== p.port),
                                })
                              }
                            />
                            {p.port}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
        </div>

        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {editing && !describing && (
            <Button variant="primary" onClick={startSaveClassification}>
              Save
            </Button>
          )}
          {needsChassisWrite &&
            (confirming ? (
              <>
                <span className="mr-auto text-sm text-warn">Are you sure? This runs on {oltName}.</span>
                <Button
                  variant="danger"
                  onClick={() => {
                    // The dialog closes and the chassis write runs in the
                    // background tray — no inline spinner, no transcript to dismiss.
                    onClose();
                    tasks.start<CliActionResult>({
                      label: many
                        ? `Add VLANs on ${oltName}`
                        : editing
                          ? `Rename VLAN ${vlanId} on ${oltName}`
                          : `Add VLAN ${vlanId} on ${oltName}`,
                      run: () => {
                        const path = many ? `${base}/bulk` : editing ? `${base}/${vlan!.vlanId}` : base;
                        return editing && !many
                          ? api.patch<CliActionResult>(path, body(false))
                          : api.post<CliActionResult>(path, body(false));
                      },
                      done: (r) =>
                        r.warnings.length ? `applied with ${r.warnings.length} warning(s)` : 'applied',
                      invalidate: [['olt', oltId, 'vlans']],
                    });
                  }}
                >
                  Yes, apply to OLT
                </Button>
              </>
            ) : (
              <Button variant="danger" onClick={() => setConfirming(true)}>
                Apply
              </Button>
            ))}
        </footer>
      </div>
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/** Deleting VLANs: previewed like every other chassis write, and refused when in use. */
function DeleteVlansDialog({
  oltId,
  oltName,
  vlanIds,
  onClose,
  onDone,
}: {
  oltId: string;
  oltName: string;
  vlanIds: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const tasks = useTasks();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            Delete {vlanIds.length} {vlanIds.length === 1 ? 'VLAN' : 'VLANs'}
          </h2>
          <p className="mt-0.5 font-mono text-xs text-muted">{vlanIds.join(', ')}</p>
        </header>
        <div className="space-y-4 p-5">
          <p className="text-sm text-muted">
            A VLAN with ONUs attached is refused — move them off it first.
          </p>
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {confirming ? (
            <>
              <span className="mr-auto text-sm text-warn">
                Delete {vlanIds.length} VLAN{vlanIds.length === 1 ? '' : 's'} on {oltName}?
              </span>
              <Button
                variant="danger"
                onClick={() => {
                  // Clear the selection, close, and run the delete in the tray.
                  onDone();
                  onClose();
                  tasks.start<CliActionResult>({
                    label: `Delete ${vlanIds.length} VLAN(s) on ${oltName}`,
                    run: () =>
                      api.delete<CliActionResult>(`/olts/${encodeURIComponent(oltId)}/vlans`, {
                        vlanIds,
                        dryRun: false,
                      }),
                    done: (r) =>
                      r.warnings.length ? `deleted with ${r.warnings.length} warning(s)` : 'deleted',
                    invalidate: [['olt', oltId, 'vlans']],
                  });
                }}
              >
                Yes, delete on OLT
              </Button>
            </>
          ) : (
            <Button variant="danger" onClick={() => setConfirming(true)}>
              Delete
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}

/**
 * Find the management VLAN by reading the chassis.
 *
 * The evidence is shown before anything is recorded, because the answer is
 * inferred rather than stated: a VLAN qualifies only when every ONU carrying it
 * does so on a management virtual port. Applying ticks Mgmt/VoIP on the ones it
 * is sure of and leaves every other VLAN exactly as the operator set it.
 *
 * The read stays in the dialog — it is the evidence. Applying is the write, so
 * it CLOSES the dialog and runs in the tasks tray.
 */
function DetectDialog({
  oltId,
  oltName,
  onClose,
}: {
  oltId: string;
  oltName: string;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [result, setResult] = useState<DetectResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () =>
      api.post<DetectResponse>(`/olts/${encodeURIComponent(oltId)}/vlans/detect`, {
        apply: false,
      }),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  });

  const carried = (result?.items ?? []).filter((i) => i.onuCount > 0);
  const found = carried.filter((i) => i.isManagement);

  const startApply = () => {
    const vlanIds = found.map((f) => f.vlanId);
    onClose();
    tasks.start<DetectResponse>({
      label: `Mark management VLAN on ${oltName}`,
      run: () =>
        api.post<DetectResponse>(`/olts/${encodeURIComponent(oltId)}/vlans/detect`, {
          apply: true,
        }),
      done: (r) =>
        r.applied > 0
          ? `Marked ${r.applied} ${r.applied === 1 ? 'VLAN' : 'VLANs'} as Mgmt/VoIP on ${oltName}.`
          : `Nothing marked on ${oltName} — VLAN ${vlanIds.join(', ')} was already ticked, or the re-read disagreed.`,
      invalidate: [['olt', oltId, 'vlans']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Detect the management VLAN</h2>
          <p className="mt-0.5 text-xs text-muted">
            No VLAN carries a flag saying it is the management one. What the chassis does say is
            how each ONU is wired — a VLAN every ONU carries on a management virtual port, never on
            vport 1, is the one the OLT manages over. This reads that, one command per VLAN, and
            writes nothing to the chassis.
          </p>
        </header>

        <div className="space-y-4 p-5">
          {run.isPending && (
            <div className="flex items-center justify-center gap-3 p-6">
              <Spinner />
              <span className="text-sm text-muted">Reading how the ONUs are wired…</span>
            </div>
          )}

          {result && !result.vendorSupported && (
            <p className="text-sm text-warn">{result.warnings[0]}</p>
          )}

          {result?.vendorSupported && (
            <>
              {result.applied > 0 && (
                <div className="rounded-lg border border-success/30 bg-success-soft px-3 py-2 text-sm text-success">
                  Marked {result.applied} {result.applied === 1 ? 'VLAN' : 'VLANs'} as Mgmt/VoIP.
                </div>
              )}
              {carried.length === 0 ? (
                <EmptyState
                  title="No ONU carries any VLAN here"
                  description="Nothing to infer from. Run Sync inventory, or tick the management VLAN by hand."
                />
              ) : (
                <Table>
                  <THead>
                    <th className="th">VLAN</th>
                    <th className="th">ONUs</th>
                    <th className="th">Virtual ports</th>
                    <th className="th">Verdict</th>
                  </THead>
                  <TBody>
                    {carried.map((i) => (
                      <TRow key={i.vlanId}>
                        <td className="td font-mono text-xs text-ink">{i.vlanId}</td>
                        {/* Distinct ONUs, with the chassis's own entry total
                            underneath when the two differ. The difference is a
                            count of EXTRA PRINTINGS, not of ONUs that were
                            printed twice: one ONU printed three times leaves
                            two extras behind and is still one ONU. */}
                        <td className="td tabular-nums text-muted">
                          {i.onuCount}
                          {i.entryCount > i.onuCount && (
                            <div
                              className="text-[10px] text-faint"
                              title={`The chassis printed ${i.entryCount} entries for these ${i.onuCount} ONUs — ${i.entryCount - i.onuCount} more entries than ONUs. It lists an ONU once per virtual port it carries the VLAN on.`}
                            >
                              {i.entryCount} entries
                            </div>
                          )}
                        </td>
                        <td className="td font-mono text-xs text-muted">
                          {i.vports.join(', ')}
                          {i.vports.length > 1 && (
                            <div className="text-[10px] text-faint">
                              {i.vports.map((v) => `${v}: ${i.onusByVport[v] ?? 0}`).join(' · ')}
                            </div>
                          )}
                        </td>
                        <td className="td text-xs text-muted">
                          {i.isManagement && (
                            <span className="mr-1.5 font-medium text-success">management —</span>
                          )}
                          {i.reason}
                        </td>
                      </TRow>
                    ))}
                  </TBody>
                </Table>
              )}
              {result.warnings.map((w) => (
                <p key={w} className="text-xs text-warn">
                  {w}
                </p>
              ))}
            </>
          )}

          {error && <ErrorNotice error={new Error(error)} />}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!result && (
            <Button variant="primary" loading={run.isPending} onClick={() => run.mutate()}>
              Read the chassis
            </Button>
          )}
          {result?.vendorSupported && found.length > 0 && (
            <Button variant="primary" onClick={startApply}>
              Mark VLAN {found.map((f) => f.vlanId).join(', ')} as Mgmt/VoIP
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}
